import { Button, Stack, styled, Typography, useTheme } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import React from 'react';
import { DynamicIcon, EllipsesMenu } from 'src/components';
import {
  getBannerActionSx,
  getComposerBannerStyles,
} from 'src/pages/Conversations/Chat/shared-styles';

export type SuggestedReplyBannerState = 'available' | 'loaded';

interface SuggestedReplyBannerProps {
  sourceLabel?: string;
  state: SuggestedReplyBannerState;
  onView: () => void;
  onDismiss: () => void;
  onRestorePersonalDraft?: () => void;
}

// Tertiary is the "machine" hue: instantly tellable from the teal Send Later banner sharing the slot.
const BannerContainer = styled(Stack)(({ theme }) =>
  getComposerBannerStyles(theme, theme.palette.custom.OV.sys.tertiary),
);

export const SuggestedReplyBanner = ({
  sourceLabel,
  state,
  onView,
  onDismiss,
  onRestorePersonalDraft,
}: SuggestedReplyBannerProps): React.JSX.Element => {
  const theme = useTheme();
  const isLoaded = state === 'loaded';

  return (
    <BannerContainer direction="row" data-testid="suggested-reply-banner">
      <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
        <DynamicIcon library="lucide" name={isLoaded ? 'check' : 'sparkles'} size={15} />
        <Typography variant="body2" noWrap>
          <Typography component="span" variant="body2" fontWeight={600}>
            Suggested reply
          </Typography>
          {sourceLabel ? (
            <Typography component="span" variant="body2" sx={{ opacity: 0.75 }}>
              {` from ${sourceLabel}`}
            </Typography>
          ) : null}
          {isLoaded ? (
            <Typography component="span" variant="body2" sx={{ opacity: 0.75 }}>
              {' · in the composer'}
            </Typography>
          ) : null}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1}>
        {isLoaded ? null : (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onView}
            sx={getBannerActionSx(theme)}
          >
            View message
          </Button>
        )}
        <V2ThemeProvider>
          <EllipsesMenu
            options={[
              {
                label: 'Dismiss',
                hint: 'Removes the suggested reply for everyone on this conversation',
                icon: 'trash-2',
                iconLibrary: 'lucide',
                onClick: onDismiss,
              },
              ...(onRestorePersonalDraft
                ? [
                    {
                      label: 'Restore my draft',
                      icon: 'rotate-ccw',
                      iconLibrary: 'lucide' as const,
                      onClick: onRestorePersonalDraft,
                    },
                  ]
                : []),
            ]}
          />
        </V2ThemeProvider>
      </Stack>
    </BannerContainer>
  );
};
